import React, { useState } from "react";
import { Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient } from "@tanstack/react-query";

interface ProfileQuoteProps {
  profile: any;
  isOwnProfile: boolean;
}

const ProfileQuote: React.FC<ProfileQuoteProps> = ({ profile, isOwnProfile }) => {
  const [isEditingQuote, setIsEditingQuote] = useState(false);
  const [quote, setQuote] = useState(profile?.favorite_quote || "");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const handleQuoteSubmit = async () => {
    try {
      const { error } = await supabase
        .from("profiles")
        .update({ favorite_quote: quote })
        .eq("id", profile.id);

      if (error) throw error;

      toast({
        title: "Quote updated",
        description: "Your favorite quote has been updated successfully.",
      });

      setIsEditingQuote(false);
      queryClient.invalidateQueries({ queryKey: ["profile", profile.wallet_address] });
    } catch (error) {
      console.error("Error updating quote:", error);
      toast({
        title: "Error",
        description: "Failed to update quote. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="relative">
      {isEditingQuote ? (
        <div className="space-y-2">
          <textarea
            value={quote}
            onChange={(e) => setQuote(e.target.value)}
            className="w-full p-2 bg-black/20 backdrop-blur-sm rounded-md text-sm text-center"
            placeholder="Enter your favorite quote..."
            rows={2}
          />
          <div className="flex gap-2 justify-center">
            <Button size="sm" onClick={handleQuoteSubmit}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setIsEditingQuote(false)}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div 
          className="relative text-center italic text-sm text-gray-400 hover:text-gray-300 cursor-default group"
          onClick={() => isOwnProfile && setIsEditingQuote(true)}
        >
          {profile?.favorite_quote ? (
            <q>{profile.favorite_quote}</q>
          ) : (
            isOwnProfile && (
              <span className="text-gray-500">Click to add your favorite quote</span>
            )
          )}
          {isOwnProfile && (
            <Button
              size="icon"
              variant="ghost"
              className="absolute md:-right-8 right-0 top-1/2 -translate-y-1/2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={() => setIsEditingQuote(true)}
            >
              <Pencil className="h-3 w-3" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProfileQuote;